import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';

import { parseReportSearch, type ReportSearch } from '../../features/reports/range';
import { CommunicationsReportScreen } from '../../features/reports/report-screens';
import { REPORT_PERMISSIONS } from '../../features/reports/types';
import { RequirePermission } from '../../lib/session/guards';
import { usePermissions } from '../../lib/session/permissions';

export const Route = createFileRoute('/reports/communications')({
  validateSearch: (search: Record<string, unknown>): ReportSearch => parseReportSearch(search),
  component: CommunicationsReportPage,
});

/**
 * `/reports/communications` — emails, text messages and reminder coverage (FR-REPORT-03).
 *
 * `reports.communications` opens it. There is **no link to a patient on this screen and
 * no contact detail on it**: M11.6 returns opt-outs aggregated by channel and reason
 * rather than a row per contact, because a masked address is still an identifier and this
 * payload is the source of a file that leaves the building. The list of *who* is on the
 * patient record, behind `patients.read`, where reading it is audited
 * (`ADMIN_REPORTS_UI.md` §7A.4).
 */
function CommunicationsReportPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const now = useMemo(() => new Date(), []);
  const { can } = usePermissions();

  return (
    <RequirePermission keys={[REPORT_PERMISSIONS.communications]}>
      <CommunicationsReportScreen
        search={search}
        onSearchChange={(next) => {
          void navigate({ to: '/reports/communications', search: next, replace: true });
        }}
        now={now}
        can={can}
        backLink={
          <Link to="/reports" search={search} className="text-primary-accessible hover:underline">
            Reports
          </Link>
        }
      />
    </RequirePermission>
  );
}
